import { useEffect, useState } from 'react';
import { aiAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { AiStatus } from '../types/ai';

/**
 * Az AI elérhetősége a bejelentkezett felhasználónak. Egyszer kérjük le és
 * minden komponens ugyanazt az állapotot kapja; a beállítások mentése és egy-egy
 * AI hívás után a refreshAiStatus() frissíti (pl. a megmaradt napi keret miatt).
 * Csak a stábnak (admin, edző) kérjük le - másnak az AI gombok nem jelennek meg.
 */
const UNAVAILABLE: AiStatus = { available: false, model: null, remainingToday: 0, personalDataAllowed: false };

let current: AiStatus | null = null;
// Kinek a státusza van a tárban - másik felhasználó belépésekor újra kell kérni
let currentUserId: number | null = null;
let pending: Promise<void> | null = null;
const listeners = new Set<(status: AiStatus) => void>();

const load = () => {
  if (!pending) {
    pending = aiAPI
      .getStatus()
      .then((status) => {
        current = status;
      })
      .catch(() => {
        current = UNAVAILABLE;
      })
      .finally(() => {
        pending = null;
        listeners.forEach((listener) => listener(current ?? UNAVAILABLE));
      });
  }
  return pending;
};

export const refreshAiStatus = () => load();

export const useAiStatus = (): AiStatus => {
  const { user } = useAuth();
  const isStaff = !!user && (user.isAdmin || user.isCoach);
  const [status, setStatus] = useState<AiStatus>(current ?? UNAVAILABLE);

  useEffect(() => {
    if (!isStaff || !user) return;
    if (currentUserId !== user.id) {
      currentUserId = user.id;
      current = null;
    }
    listeners.add(setStatus);
    if (current) setStatus(current);
    else load();
    return () => {
      listeners.delete(setStatus);
    };
  }, [isStaff, user]);

  return isStaff ? status : UNAVAILABLE;
};
